import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Newspaper, ArrowLeft, RefreshCw, ExternalLink, Clock, Tag as TagIcon,
  AlertCircle, TrendingUp, Lightbulb, Shield, Car, Home, Heart, Stethoscope,
  Building2, Briefcase, Sparkles, Info, FileText
} from "lucide-react";
import { toast } from "sonner";
import InstagramRedazioneStrip from "@/components/InstagramRedazioneStrip";
import SEOHead from "@/components/SEOHead";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import { schemaBreadcrumb } from "@/lib/siteSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIA_ICON = {
  Auto: Car,
  Casa: Home,
  Vita: Heart,
  Salute: Stethoscope,
  Aziende: Building2,
  Professionisti: Briefcase,
  Multirischio: Shield,
  Previdenza: Sparkles,
};

const CATEGORIA_COLOR = {
  Auto: "text-blue-700 bg-blue-50 border-blue-200",
  Casa: "text-amber-700 bg-amber-50 border-amber-200",
  Vita: "text-rose-700 bg-rose-50 border-rose-200",
  Salute: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Aziende: "text-purple-700 bg-purple-50 border-purple-200",
  Professionisti: "text-indigo-700 bg-indigo-50 border-indigo-200",
  Multirischio: "text-zinc-700 bg-zinc-50 border-zinc-200",
  Previdenza: "text-teal-700 bg-teal-50 border-teal-200",
};

export default function NotizieAssicurativePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/notizie-assicurative`, {
        params: forceRefresh ? { refresh: true } : {},
        withCredentials: true,
      });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Impossibile caricare le notizie");
      toast.error(err.response?.data?.detail || "Errore caricamento notizie");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(false); }, []);

  const fmtUpdated = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }) : "";

  // Compliance IVASS feb-2026 — titolo dinamico basato su freshness reale del DB (updated_at)
  const isFresh = (() => {
    if (!data?.updated_at) return false;
    try {
      const t = new Date(data.updated_at);
      const now = new Date();
      return t.toDateString() === now.toDateString();
    } catch { return false; }
  })();
  const pageTitle = isFresh
    ? "Notizie assicurative del giorno"
    : "Ultime notizie assicurative disponibili";
  const pageSubtitle = "Analisi ragionata delle principali notizie italiane, lette da una prospettiva assicurativa. Per comprendere i rischi emergenti e gli aspetti delle proprie coperture che può essere utile verificare.";

  return (
    <>
      <SEOHead
        title="Notizie Assicurative | Analisi Quotidiana a cura di Colucci"
        description="Ogni ora analizziamo le notizie italiane (ANSA, Il Sole 24 Ore) dal punto di vista assicurativo: cosa cambia per la tua polizza auto, casa, vita, professionale. Analisi con fonti verificate."
        canonical="/notizie-assicurative"
        keywords={[
          "notizie assicurative italia",
          "analisi assicurativa",
          "news IVASS",
          "impatto assicurativo notizie",
          "polizza auto casa vita",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Notizie Assicurative",
            description:
              "Analisi ragionata delle notizie italiane con ottica assicurativa. Aggiornata ogni ora con fonti tracciabili.",
            url: "https://www.colucciassicuratori.com/notizie-assicurative",
            publisher: {
              "@type": "InsuranceAgency",
              name: "Colucci Assicuratori",
              url: "https://www.colucciassicuratori.com/",
            },
            dateModified: data?.updated_at || new Date().toISOString(),
            inLanguage: "it-IT",
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: data?.notizie?.length || 0,
              itemListElement: (data?.notizie || []).slice(0, 10).map((n, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                  "@type": "NewsArticle",
                  headline: n.titolo_originale || n.titolo || "Notizia assicurativa",
                  description: (n.sintesi_notizia || "").slice(0, 300),
                  articleSection: n.categoria || "Assicurazioni",
                  keywords: n.tag || "",
                  publisher: { "@type": "Organization", name: n.fonte || "Colucci Assicuratori" },
                  mainEntityOfPage: n.link || "https://www.colucciassicuratori.com/notizie-assicurative",
                },
              })),
            },
          },
          schemaBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Notizie Assicurative", path: "/notizie-assicurative" },
          ]),
        ]}
      />
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="notizie-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10">
          <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1 mb-6" data-testid="notizie-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna alla Home
          </Link>

          {/* HERO */}
          <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-[#163321]/10 rounded-2xl flex items-center justify-center shrink-0">
                <Newspaper className="w-7 h-7 text-[#163321]" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-medium text-[#0F1F14] leading-tight" data-testid="notizie-page-title">{pageTitle}</h1>
                <p className="text-sm text-[#4A5E51] mt-1 max-w-xl" data-testid="notizie-page-subtitle">{pageSubtitle}</p>
                {data?.updated_at && (
                  <p className="text-xs text-[#4A5E51] mt-2 flex items-center gap-1" data-testid="notizie-updated">
                    <Clock className="w-3 h-3" /> {isFresh
                      ? `Aggiornato il ${fmtUpdated(data.updated_at)}`
                      : `Ultimo aggiornamento disponibile: ${fmtUpdated(data.updated_at)}. Il servizio di aggiornamento odierno è temporaneamente non disponibile.`}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchNews(true)} disabled={loading} className="rounded-full border-[#E5E2DB]" data-testid="notizie-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>

          {/* BANNER CAROSELLI IG */}
          <a href="https://www.instagram.com/colucci_assicuratori/" target="_blank" rel="noopener noreferrer"
            className="group block bg-gradient-to-r from-[#0F1F14] via-[#1d4a2e] to-[#0F1F14] rounded-2xl p-5 mb-6 relative overflow-hidden hover:shadow-xl transition-shadow" data-testid="notizie-caroselli-banner">
            <div className="absolute top-0 right-0 w-60 h-60 rounded-full bg-[#D97B58]/15 blur-[80px]" />
            <div className="relative flex items-start sm:items-center gap-4 flex-col sm:flex-row">
              <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] via-40% to-[#DD2A7B] p-0.5 shrink-0">
                <div className="w-full h-full rounded-full bg-[#0F1F14] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97B58] mb-1">Le informazioni chiave in carosello</p>
                <p className="text-white font-heading text-base sm:text-lg font-semibold leading-tight">
                  Ogni news di questa pagina diventa un carosello Instagram su <span className="text-[#D97B58]">@colucci_assicuratori</span>
                </p>
                <p className="text-white/60 text-xs sm:text-sm mt-1">Salva e condividi le informazioni assicurative chiave in formato visual, pronte da inoltrare.</p>
              </div>
              <div className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-gradient-to-tr from-[#FEDA77] via-[#F58529] via-40% to-[#DD2A7B] text-white text-xs font-semibold group-hover:scale-[1.03] transition-transform shrink-0">
                Apri Instagram <ArrowLeft className="w-3.5 h-3.5 rotate-[135deg]" />
              </div>
            </div>
          </a>

          {/* NOTA EDITORIALE + AVVERTENZA ASSICURATIVA (compliance IVASS feb-2026 Step 3)
              Workflow editoriale ora ATTIVO: notizie generate dall'IA passano in stato
              in_review → approvate manualmente da operatore staff → published.
              Solo articoli approvati sono mostrati qui. */}
          <div className="bg-[#FFF7E8] border border-[#E8D3A0] rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="notizie-nota-editoriale">
            <Info className="w-5 h-5 text-[#B8820E] shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-2">
              <p className="text-xs text-[#8B6100] leading-relaxed">
                <strong>Nota editoriale.</strong> I contenuti sono elaborati a partire da
                fonti giornalistiche e istituzionali selezionate, con il supporto di sistemi
                di intelligenza artificiale, e sono <strong>sottoposti a verifica editoriale</strong>
                prima della pubblicazione. Colucci Assicuratori mantiene la responsabilità
                editoriale dei testi pubblicati. Le notizie sono sintetizzate e commentate
                autonomamente; le fonti originali sono indicate al termine di ciascun contenuto.
              </p>
            </div>
          </div>
          <div className="bg-[#F5F5F0] border border-[#D9D5C7] rounded-2xl p-4 mb-8 flex items-start gap-3" data-testid="notizie-avvertenza-assicurativa">
            <AlertCircle className="w-5 h-5 text-[#4A5E51] shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-xs text-[#4A5E51] leading-relaxed">
                <strong>Avvertenza assicurativa.</strong> Le informazioni e le considerazioni
                assicurative hanno carattere generale e divulgativo. Non costituiscono una
                raccomandazione personalizzata, una proposta contrattuale o una valutazione di
                adeguatezza relativa a uno specifico prodotto. Garanzie, esclusioni, franchigie,
                scoperti, massimali e condizioni di operatività variano in base al contratto.
                Per valutazioni riferite alle proprie esigenze è necessario rivolgersi a un
                intermediario assicurativo abilitato e consultare la documentazione del prodotto.
              </p>
            </div>
          </div>

          {/* LOADING */}
          {loading && !data && (
            <div className="flex items-center justify-center py-20" data-testid="notizie-loading">
              <div className="w-10 h-10 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* ERROR */}
          {error && !data && (
            <div className="bg-white border border-red-200 rounded-2xl p-8 text-center" data-testid="notizie-error">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
              <p className="text-sm text-[#0F1F14] font-medium mb-1">Non riusciamo a recuperare le notizie in questo momento</p>
              <p className="text-xs text-[#4A5E51]">{error}</p>
              <Button onClick={() => fetchNews(false)} className="mt-4 bg-[#163321] text-white rounded-full" data-testid="notizie-retry">Riprova</Button>
            </div>
          )}

          {/* WARNING (se cache stale) */}
          {data?.warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {data.warning}
            </div>
          )}

          {/* NEWS LIST */}
          {data?.notizie?.length > 0 && (
            <div className="space-y-5" data-testid="notizie-list" aria-live="polite">
              {data.notizie.map((n, i) => {
                const Icon = CATEGORIA_ICON[n.categoria] || Shield;
                const catColor = CATEGORIA_COLOR[n.categoria] || CATEGORIA_COLOR.Multirischio;
                const titolo = n.titolo_redazionale || n.titolo || n.titolo_originale || "Notizia assicurativa";
                const fontiDett = Array.isArray(n.fonti_dettaglio) ? n.fonti_dettaglio : [];
                // Validate primary link: nascondi se path è home
                const primaryLink = (() => {
                  const u = n.link || "";
                  try {
                    const p = new URL(u).pathname;
                    if (!u || !p || p === "/" || p === "/home" || p === "/index") return "";
                    return u;
                  } catch { return ""; }
                })();
                return (
                  <article
                    key={i}
                    className="bg-white border border-[#E5E2DB] rounded-2xl p-6 hover:border-[#163321]/30 transition-colors focus-within:ring-2 focus-within:ring-[#163321]/30"
                    data-testid={`notizia-${i}`}
                    aria-labelledby={`notizia-titolo-${i}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${catColor}`} data-testid={`notizia-categoria-${i}`}>
                          <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {n.categoria}
                        </span>
                        {n.rilevanza === "alta" && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#163321] text-white">
                            <TrendingUp className="w-3 h-3" aria-hidden="true" /> Alta rilevanza
                          </span>
                        )}
                        {n.tag && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#4A5E51]">
                            <TagIcon className="w-3 h-3" aria-hidden="true" /> {n.tag}
                          </span>
                        )}
                      </div>
                      {n.fonte && <span className="text-[11px] text-[#4A5E51] uppercase tracking-wider">{n.fonte}</span>}
                    </div>

                    <h2
                      id={`notizia-titolo-${i}`}
                      className="font-heading text-lg text-[#0F1F14] font-medium leading-tight mb-3"
                      data-testid={`notizia-titolo-${i}`}
                    >
                      {titolo}
                    </h2>

                    {n.descrizione && (
                      <p className="text-sm text-[#4A5E51] leading-relaxed mb-5 italic">{n.descrizione}</p>
                    )}

                    <div className="bg-[#F5F8F6] border border-[#163321]/15 rounded-xl p-4 mb-4" role="region" aria-label="Lettura assicurativa">
                      <h3 className="text-[10px] uppercase tracking-wider text-[#163321] font-bold mb-2 flex items-center gap-1">
                        <Shield className="w-3 h-3" aria-hidden="true" /> Lettura assicurativa
                      </h3>
                      <p className="text-sm text-[#0F1F14] leading-relaxed whitespace-pre-wrap">{n.impatto_assicurativo || n.analisi}</p>
                    </div>

                    {/* Nota standard per articolo (compliance IVASS feb-2026) */}
                    <p className="text-sm text-neutral-600 italic mb-4" data-testid={`notizia-nota-coperture-${i}`}>
                      L&apos;operatività delle coperture dipende dalle condizioni del singolo
                      contratto, dalle esclusioni previste, dai limiti di indennizzo e dalle
                      circostanze concrete dell&apos;evento.
                    </p>

                    {n.azione_suggerita && (
                      <div className="bg-[#FFF8E1] border border-[#E8D3A0]/60 rounded-xl p-4 mb-4" role="region" aria-label="Cosa puoi valutare">
                        <h3 className="text-[10px] uppercase tracking-wider text-[#8B6100] font-bold mb-2 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" aria-hidden="true" /> Cosa puoi valutare
                        </h3>
                        <p className="text-sm text-[#0F1F14] leading-relaxed">{n.azione_suggerita}</p>
                      </div>
                    )}

                    {/* FONTI complete (formato IVASS-compliant) — H3 unica "Fonti"
                        include sia fonti_dettaglio (testate/autori/URL) che riferimenti
                        citati nel testo (fonti_dati: art. leggi, dati numerici) come sub-lista. */}
                    {(fontiDett.length > 0 || n.fonti_dati?.length > 0) && (
                      <div className="mb-4 pt-3 border-t border-dashed border-[#E5E2DB]" data-testid={`notizia-fonti-${i}`}>
                        <h3 className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-bold mb-2 flex items-center gap-1">
                          <FileText className="w-3 h-3" aria-hidden="true" /> Fonti
                        </h3>
                        {fontiDett.length > 0 && (
                          <ul className="space-y-1.5">
                            {fontiDett.map((f, idx) => {
                              const dataPubbl = (f.data_pubblicazione || "").slice(0, 10);
                              const dataConsult = (f.data_consultazione || "").slice(0, 10);
                              const fmtDate = (d) => {
                                if (!d) return "";
                                try {
                                  const [y, m, day] = d.split("-");
                                  if (y && m && day) return `${day}/${m}/${y}`;
                                  return d;
                                } catch { return d; }
                              };
                              const isIstituzionale = f.tipo === "istituzionale";
                              const parts = [];
                              if (f.testata) parts.push(<b key="t">{f.testata}</b>);
                              if (f.autore)  parts.push(<span key="a"> — {f.autore}</span>);
                              else if (f.testata) parts.push(<span key="a"> — n.d.</span>);
                              if (f.titolo)  parts.push(<span key="tit">, «{f.titolo}»</span>);
                              if (dataPubbl) parts.push(<span key="dp">, pubblicato il {fmtDate(dataPubbl)}</span>);
                              if (dataConsult) parts.push(<span key="dc">. Consultato il {fmtDate(dataConsult)}</span>);
                              return (
                                <li
                                  key={idx}
                                  className={`text-[11px] text-[#4A5E51] leading-relaxed ${isIstituzionale ? "bg-[#F5F8F6] border border-[#163321]/15 rounded p-2" : ""}`}
                                  data-testid={`notizia-${i}-fonte-${idx}`}
                                >
                                  {isIstituzionale && <span className="text-[10px] font-bold uppercase text-[#163321] block mb-0.5">Fonte primaria istituzionale</span>}
                                  {parts}
                                  {f.url && (() => {
                                    try {
                                      const p = new URL(f.url).pathname;
                                      if (!p || p === "/" || p === "/home") return null;
                                    } catch { return null; }
                                    return (
                                      <>
                                        {" "}
                                        <a
                                          href={f.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="underline text-[#163321] hover:text-[#0F1F14] inline-flex items-center gap-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#163321]/40 rounded-sm"
                                          aria-label={`Apri fonte esterna ${f.testata || "originale"}`}
                                        >
                                          apri <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                        </a>
                                      </>
                                    );
                                  })()}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {n.fonti_dati?.length > 0 && (
                          <div className={fontiDett.length > 0 ? "mt-3 pt-2 border-t border-dashed border-[#E5E2DB]/60" : ""} data-testid={`notizia-fonti-citate-${i}`}>
                            <p className="text-[10px] uppercase tracking-wider text-[#4A5E51]/80 font-semibold mb-1">Dati e norme citati nel testo</p>
                            <ul className="space-y-1">
                              {n.fonti_dati.map((f, idx) => (
                                <li key={idx} className="text-[11px] text-[#4A5E51] flex items-start gap-1.5">
                                  <span className="text-[#163321] mt-0.5" aria-hidden="true">•</span>
                                  <span className="italic">{f}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* LINK primario "Leggi la fonte originale" — solo se URL specifico */}
                    {primaryLink ? (
                      <a
                        href={primaryLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-[#163321] hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#163321]/40 rounded-sm"
                        data-testid={`notizia-link-${i}`}
                        aria-label="Leggi la fonte originale in una nuova scheda"
                      >
                        <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /> Leggi la fonte originale
                      </a>
                    ) : (n.link && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-[#8B6100] bg-[#FFF7E8] border border-[#E8D3A0] rounded-full px-2 py-0.5" data-testid={`notizia-link-warn-${i}`} title="Il link fornito dalla fonte punta a una pagina generica; consulta la testata per l'articolo specifico.">
                        <AlertCircle className="w-3 h-3" aria-hidden="true" /> link fonte non specifico
                      </span>
                    ))}

                    {/* CTA separata (feb-2026 Step 5) — visualmente distinta dalla notizia */}
                    <div className="mt-6 pt-4 border-t border-neutral-200 bg-neutral-50 rounded-md px-4 py-3" data-testid={`notizia-cta-${i}`}>
                      <p className="text-sm text-neutral-700">
                        Desideri una valutazione riferita alla tua situazione?{" "}
                        <Link
                          to="/consulenza"
                          className="underline font-medium text-[#163321] hover:text-[#0F1F14] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#163321]/40 rounded-sm"
                          data-testid={`notizia-cta-link-${i}`}
                        >
                          Prenota un appuntamento con un intermediario
                        </Link>.
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* EMPTY STATE */}
          {data && (!data.notizie || data.notizie.length === 0) && !loading && (
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-8 text-center text-sm text-[#4A5E51]">
              Nessuna notizia con impatto assicurativo rilevante è stata selezionata oggi. Riprova più tardi.
            </div>
          )}
        </div>

        {/* STRIP INSTAGRAM REDAZIONE - connette le news al canale IG */}
        <InstagramRedazioneStrip
          variant="light"
          title="Preferisci in video o in carosello? Le stesse news su Instagram."
          subtitle="La redazione trasforma ogni notizia in un carosello con le informazioni chiave o in un reel da 60 secondi. Salvale, condividile, tienile a portata di mano sul nostro profilo @colucci_assicuratori."
          limit={4}
        />

        <div className="max-w-5xl mx-auto px-5 sm:px-6 pb-10">
          {/* FOOTER INFO */}
          {data?.fonti_consultate?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-[#E5E2DB] text-center">
              <p className="text-[11px] text-[#4A5E51]">
                Fonti consultate: {data.fonti_consultate.join(" · ")}
              </p>
              <p className="text-[11px] text-[#4A5E51] mt-1">
                Per una consulenza personalizzata <Link to="/consulenza" className="text-[#163321] underline">prenota un appuntamento</Link> o chiama lo <a href="tel:0817611000" className="text-[#163321] underline">081/7611000</a>
              </p>
            </div>
          )}

          {/* NEWSLETTER — iscrizione digest settimanale */}
          <div className="mt-10">
            <NewsletterSubscribe fonte="notizie-assicurative" />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
